/**
 * User Profile Page - View and edit user profile information
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Building2,
  Calendar,
  Save,
  Loader2,
  Camera,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Profile = () => {
  const { user, profile, session, organization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '',
    avatar_url: '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || '',
        avatar_url: profile.avatar_url || '',
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success('Profile updated successfully');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const initials = (formData.display_name || user?.email?.split('@')[0] || 'U')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-cloud-white">My Profile</h1>
        <p className="text-soft-silver text-sm mt-1">Manage your account information</p>
      </div>

      {/* Profile Card */}
      <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] overflow-hidden">
        {/* Avatar Section */}
        <div className="p-6 border-b border-white/[0.06] bg-gradient-to-r from-electric-ice-blue/5 to-transparent">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-2 border-white/[0.08]">
                {formData.avatar_url && (
                  <AvatarImage src={formData.avatar_url} alt={formData.display_name} />
                )}
                <AvatarFallback className="bg-obsidian text-soft-silver text-2xl">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-cloud-white">
                {formData.display_name || 'Set your name'}
              </h2>
              <p className="text-soft-silver">{user?.email}</p>
              {profile?.platform_role && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Shield className="w-4 h-4 text-electric-ice-blue" />
                  <span className="text-xs text-electric-ice-blue font-medium uppercase tracking-wider">
                    {profile.platform_role.replace('_', ' ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid gap-6">
            {/* Display Name */}
            <div className="space-y-2">
              <Label htmlFor="display_name" className="text-soft-silver flex items-center gap-2">
                <User className="w-4 h-4" />
                Display Name
              </Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="Enter your name"
                className="bg-obsidian/50 border-white/[0.08] text-cloud-white"
              />
            </div>

            {/* Email (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-soft-silver flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-obsidian/50 border-white/[0.08] text-muted-text"
              />
              <p className="text-xs text-muted-text">Email cannot be changed</p>
            </div>

            {/* Avatar URL */}
            <div className="space-y-2">
              <Label htmlFor="avatar_url" className="text-soft-silver flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Avatar URL
              </Label>
              <Input
                id="avatar_url"
                value={formData.avatar_url}
                onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
                className="bg-obsidian/50 border-white/[0.08] text-cloud-white"
              />
            </div>
          </div>

          {/* Account Info */}
          <div className="pt-6 border-t border-white/[0.06]">
            <h3 className="text-sm font-medium text-soft-silver mb-4">Account Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-text">
                <Calendar className="w-4 h-4" />
                <span>Joined: {formatDate(profile?.created_at)}</span>
              </div>
              {organization && (
                <div className="flex items-center gap-2 text-muted-text">
                  <Building2 className="w-4 h-4" />
                  <span>Organization: {organization.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
